import { reportSelected } from '@components/Dashboard/dashboardSlice';
import {
  GetLci3ReportParams,
  useGetCustomReportsQuery,
  useGetLci3ReportsQuery,
} from '@components/Dashboard/dashboardSlice/campaignsApi';
import { Lci3Report } from '@components/Dashboard/dashboardSlice/schemas/lci3Reports';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from '@utils/hooks';
import { useEffect, useMemo } from 'react';
import { useCurrentChannel } from '../../../hooks/useCurrentChannel';
import { useCampaignParameters, useCurrentCampaign } from './useCampaign';
import { subdimensionId } from '../utils';
import { validDimensionSlug } from '@features/reports';
import { GetCampaignParams } from '../api';

type ReportsHook = {
  reports: Lci3Report[];
  loading: boolean;
};

export type UseReportsProps = {
  campaignId: number | string | undefined;
  campaignLoading: boolean;
  campaignParams: GetCampaignParams;
  reportParams: Omit<GetLci3ReportParams, 'id'>;
};

export const useCurrentReportsProps = (): UseReportsProps => {
  const { campaign, isFetching: campaignLoading } = useCurrentCampaign();
  const campaignParams = useCampaignParameters();
  const { channel } = useCurrentChannel();
  return useMemo(() => {
    return {
      campaignId: campaign?.id,
      campaignLoading,
      campaignParams,
      reportParams: {
        channel: channel ?? undefined,
        includeUnapproved: campaignParams.include_unapproved,
      },
    };
  }, [campaign, campaignLoading, campaignParams, channel]);
};

/**
 * Returns all reports for the current campaign, sorted by ascending report date.
 */
export const useAllCurrentReports = (): ReportsHook => {
  const props = useCurrentReportsProps();

  return useAllReports(props);
};

const useAllReports = (props: UseReportsProps): ReportsHook => {
  const { campaignId, campaignLoading, reportParams, campaignParams } = props;
  const { data: weeklyData, isFetching: weeklyFetching } =
    useGetLci3ReportsQuery(
      campaignId && !campaignParams.custom_report_id
        ? { ...reportParams, id: campaignId }
        : skipToken
    );
  const { data: customData, isFetching: customFetching } =
    useGetCustomReportsQuery(
      campaignId && campaignParams.custom_report_id
        ? { ...reportParams, id: campaignId }
        : skipToken
    );
  const { data, isFetching } = useMemo(
    () =>
      campaignParams.custom_report_id
        ? {
            data: customData,
            isFetching: customFetching,
          }
        : {
            data: weeklyData,
            isFetching: weeklyFetching,
          },
    [
      campaignParams.custom_report_id,
      customData,
      customFetching,
      weeklyData,
      weeklyFetching,
    ]
  );
  const selectedReportDate = useCurrentReportDate();
  const dispatch = useAppDispatch();
  const reports = useMemo(
    () =>
      data
        ? data.toSorted(
            (a, b) => Date.parse(a.reportDate) - Date.parse(b.reportDate)
          )
        : [],
    [data]
  );
  const latestReport = reports.at(-1);

  useEffect(() => {
    if (
      (!selectedReportDate && latestReport) ||
      (selectedReportDate &&
        latestReport &&
        !reports.map(report => report.reportDate).includes(selectedReportDate))
    ) {
      dispatch(reportSelected(latestReport.reportDate));
    }
  }, [latestReport, selectedReportDate, dispatch, reports]);

  return useMemo(() => {
    return isFetching || campaignLoading
      ? {
          reports: [],
          loading: true,
        }
      : {
          reports,
          loading: false,
        };
  }, [reports, isFetching, campaignLoading]);
};

export const useCurrentReports = (): ReportsHook => {
  const props = useCurrentReportsProps();

  return useReports(props);
};

export const useReports = (props: UseReportsProps): ReportsHook => {
  const allReportsHook = useAllReports(props);
  const selectedReportDate = useCurrentReportDate();

  const filteredReports = useMemo(() => {
    return selectedReportDate
      ? allReportsHook.reports.filter(
          report => new Date(report.reportDate) <= new Date(selectedReportDate)
        )
      : allReportsHook.reports;
  }, [allReportsHook, selectedReportDate]);

  return useMemo(() => {
    return {
      ...allReportsHook,
      reports: filteredReports,
    };
  }, [allReportsHook, filteredReports]);
};

/**
 * Returns a report corresponding to the currently selected report date.
 */
export const useReport = (props: UseReportsProps) => {
  const { reports, loading } = useReports(props);
  const sortedReports = useMemo(
    () =>
      reports.toSorted(
        (a, b) => Date.parse(b.reportDate) - Date.parse(a.reportDate)
      ),
    [reports]
  );
  const currentReport = sortedReports.at(0);
  const salesReport = sortedReports.find(report => report.salesMetrics);
  const subdimensions = useMemo(() => {
    return (currentReport?.subdimensions ?? [])
      .filter(subdimension => validDimensionSlug(subdimension.name))
      .map(subdimension => ({
        ...subdimension,
        salesMetrics:
          salesReport?.subdimensions.find(
            salesSubdimension =>
              subdimensionId(salesSubdimension) === subdimensionId(subdimension)
          )?.salesMetrics ?? null,
      }));
  }, [currentReport, salesReport]);

  return useMemo(() => {
    return {
      report: currentReport,
      salesReport,
      subdimensions,
      loading,
    };
  }, [currentReport, salesReport, subdimensions, loading]);
};

export const useCurrentReport = () => {
  const props = useCurrentReportsProps();

  return useReport(props);
};

export const useCurrentReportDate = () => {
  return useAppSelector(state => state.dashboardSlice.ui.selectedReportDate);
};
